import type Swiper from 'swiper/core'

/**
 * В freeMode штатный Navigation ходит по activeIndex.
 * После ручного скролла activeIndex часто 0 — slidePrev уезжает в начало.
 *
 * Листаем примерно на экран, оставляя крайний видимый слайд в кадре:
 * next — правый крайний становится левым; prev — левый крайний становится правым.
 */
type SwiperWithFreeModeGrid = InstanceType<typeof Swiper> & {
  slidesGrid?: number[]
  slidesSizesGrid?: number[]
  minTranslate: () => number
  maxTranslate: () => number
}

function syncFreeModeNavButtons(
  swiper: InstanceType<typeof Swiper>,
  prevEl: HTMLElement,
  nextEl: HTMLElement,
) {
  prevEl.classList.toggle('swiper-button-disabled', swiper.isBeginning)
  nextEl.classList.toggle('swiper-button-disabled', swiper.isEnd)
}

function scrollFreeModeByPage(
  swiper: InstanceType<typeof Swiper>,
  direction: 'prev' | 'next',
) {
  const freeModeSwiper = swiper as SwiperWithFreeModeGrid
  const slidesGrid = freeModeSwiper.slidesGrid || []
  const sizes = freeModeSwiper.slidesSizesGrid || []

  if (!slidesGrid.length) {
    return
  }

  const scrollLeft = -freeModeSwiper.getTranslate()
  const viewportRight = scrollLeft + freeModeSwiper.width
  let targetTranslate = freeModeSwiper.getTranslate()

  if (direction === 'next') {
    // Правый крайний видимый слайд → после сдвига у левого края
    let edgeIndex = 0

    slidesGrid.forEach((left, index) => {
      if (left < viewportRight - 1) {
        edgeIndex = index
      }
    })

    let targetLeft = slidesGrid[edgeIndex]

    // Уже у левого края (один широкий слайд) — шагнём на следующий
    if (Math.abs(targetLeft - scrollLeft) < 2 && edgeIndex < slidesGrid.length - 1) {
      targetLeft = slidesGrid[edgeIndex + 1]
    }

    targetTranslate = -targetLeft
  } else {
    // Левый крайний видимый слайд → после сдвига у правого края
    let edgeIndex = slidesGrid.findIndex(
      (left, index) => left + (sizes[index] || 0) > scrollLeft + 1,
    )

    if (edgeIndex < 0) {
      edgeIndex = 0
    }

    let slideRight = slidesGrid[edgeIndex] + (sizes[edgeIndex] || 0)
    let targetLeft = slideRight - freeModeSwiper.width

    // Уже у правого края — шагнём на предыдущий
    if (Math.abs(targetLeft - scrollLeft) < 2 && edgeIndex > 0) {
      slideRight = slidesGrid[edgeIndex - 1] + (sizes[edgeIndex - 1] || 0)
      targetLeft = slideRight - freeModeSwiper.width
    }

    targetTranslate = -targetLeft
  }

  const clamped = Math.max(
    Math.min(targetTranslate, freeModeSwiper.minTranslate()),
    freeModeSwiper.maxTranslate(),
  )

  freeModeSwiper.translateTo(clamped, 300)
}

/**
 * Вешает ручную навигацию для freeMode.
 * Возвращает функцию очистки слушателей.
 */
export function bindFreeModeNavigation({
  swiper,
  prevEl,
  nextEl,
}: {
  swiper: InstanceType<typeof Swiper>
  prevEl: HTMLElement
  nextEl: HTMLElement
}): () => void {
  const onPrevClick = (event: Event) => {
    event.preventDefault()

    if (!prevEl.classList.contains('swiper-button-disabled')) {
      scrollFreeModeByPage(swiper, 'prev')
    }
  }

  const onNextClick = (event: Event) => {
    event.preventDefault()

    if (!nextEl.classList.contains('swiper-button-disabled')) {
      scrollFreeModeByPage(swiper, 'next')
    }
  }

  const syncNav = () => syncFreeModeNavButtons(swiper, prevEl, nextEl)

  prevEl.addEventListener('click', onPrevClick)
  nextEl.addEventListener('click', onNextClick)
  swiper.on('progress', syncNav)
  swiper.on('transitionEnd', syncNav)
  swiper.on('resize', syncNav)
  syncNav()

  return () => {
    prevEl.removeEventListener('click', onPrevClick)
    nextEl.removeEventListener('click', onNextClick)
    swiper.off('progress', syncNav)
    swiper.off('transitionEnd', syncNav)
    swiper.off('resize', syncNav)
  }
}
